<template>
    <div class="form-group">
      <label for="password">{{ isRegistration ? 'Mot de passe' : 'Nouveau mot de passe' }}</label>
      <input
        id="password"
        v-model="password"
        type="password"
        :name="isRegistration ? 'password' : 'newPassword'"
        :placeholder="passwordPlaceholder"
        @input="checkPasswordStrength"
        autocomplete="new-password"
        :class="{
          success: password && strength === rules.length,
          error: password && strength < rules.length
        }"
      />

      <div v-if="password" class="strength-container">
        <div class="strength-bar">
          <div
            class="strength-fill"
            :class="strengthClass"
            :style="{ width: (strength / rules.length) * 100 + '%' }"
          ></div>
        </div>
      </div>

      <div v-if="password" class="criteria">
        <div
          v-for="rule in rules"
          :key="rule.name"
          :class="{ met: rule.test(password) }"
        >
          ✓ {{ rule.label }}
        </div>
      </div>
    </div>

    <div class="form-group">
      <label for="verify-password">Confirmer le mot de passe</label>
      <input
        id="verify-password"
        v-model="verifyPassword"
        type="password"
        :placeholder="verifyPlaceholder"
        autocomplete="new-password"
        :class="{
          success: verifyPassword && password && passwordsMatch,
          error: verifyPassword && password && !passwordsMatch
        }"
      />
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  isRegistration: {
    type: Boolean,
    default: false
  }
})

const passwordPlaceholder = computed(() =>
  props.isRegistration ? 'Choisir un mot de passe' : 'Laisser vide pour ne pas changer'
)

const verifyPlaceholder = computed(() =>
  props.isRegistration ? 'Confirmer le mot de passe' : 'Confirmer le nouveau mot de passe'
)

const password = ref('')
const verifyPassword = ref('')
const strength = ref(0)

const rules = [
  { name: 'length', label: 'Au moins 8 caractères', test: (p) => /^.{8,}$/.test(p) },
  { name: 'upper', label: 'Une lettre majuscule', test: (p) => /[A-Z]/.test(p) },
  { name: 'number', label: 'Un chiffre', test: (p) => /[0-9]/.test(p) },
  { name: 'special', label: 'Un caractère spécial', test: (p) => /[^A-Za-z0-9]/.test(p) }
]

const passwordsMatch = computed(() => password.value === verifyPassword.value)

const strengthClass = computed(() => {
  if (strength.value <= 1) return 'weak'
  if (strength.value <= 2) return 'medium'
  return 'strong'
})

function checkPasswordStrength() {
  strength.value = rules.reduce((acc, rule) => acc + (rule.test(password.value) ? 1 : 0), 0)
}

function reset() {
  password.value = ''
  verifyPassword.value = ''
  strength.value = 0
}

defineExpose({
  getValues: () => {
    const key = props.isRegistration ? 'password' : 'newPassword'
    console.log('[PasswordForm] isRegistration:', props.isRegistration, '→ key:', key)
    return {
      [key]: password.value,
      verifyPassword: verifyPassword.value,
      isValid: passwordsMatch.value && strength.value === rules.length
    }
  },
  reset
})
</script>
<style scoped>
/* Strength bar */
.strength-bar {
  height: 6px;
  background: var(--color-grey-light);
  border-radius:  3px;
  overflow:  hidden;
  margin: 0.2rem 0 0.8rem;
}
.strength-fill {
  height: 100%;
  transition: width 0.3s ease, background-color 0.3s ease;
}
.strength-fill.weak { background:  linear-gradient(90deg, #c0392b, #e74c3c); }
.strength-fill.medium { background: linear-gradient(90deg, #d68910, #e67e22); }
.strength-fill.strong { background: linear-gradient(90deg, var(--color-feuille), var(--color-jungle)); }

/* Criteria list */
.criteria {
  padding: 0.75rem;
  background: var(--color-grey-light);
  border-radius: var(--border-radius-sm);
  font-size: 0.9rem;
}
.criteria div {
  color: var(--color-text-secondary);
  margin: 0.2rem 0;
  transition: color 0.2s;
}
.criteria div.met {
  color: var(--color-jungle);
  font-weight: 600;
}
</style>
